"use client";
import useTranslate from "@app/_hooks/useTranslate";
import { CheckIcon } from "lucide-react";

import { useNavigation } from "@app/[locale]/(site)/navContext";
import { Input, Checkbox as NGGCheckbox } from "@components/ngg";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { ChangeEvent } from "react";
import { useCallback, useMemo, useRef, useState } from "react";

import { SITE_CODE } from "@/config/common";
import { useMarket } from "@features/market/useMarket";

const SEARCHABLE_THRESHOLD = 20;

type Item = {
  count?: number;
  name: string;
  label?: string;
  displayName?: string;
};

const EMPTY_ITEMS: Item[] = [];
const EMPTY_VALUES: string[] = [];

type Props = {
  items?: Item[];
  onChange: (event: ChangeEvent<HTMLInputElement>, attribute: string) => void;
  values?: string[];
  attribute: string;
  name?: string;
};

function filterElevateItems(
  items: Item[],
  attribute = "",
  countryCode: string,
  commerceNamePairs?: Record<string, string>,
) {
  if (!commerceNamePairs) return items;

  const output: Item[] = [];

  for (const item of items) {
    if (attribute === "custom.leafCategoryCodes") {
      const matchingItem = commerceNamePairs?.[item.name];
      if (matchingItem) {
        output.push({
          ...item,
          label: matchingItem,
        });
      }
    } else if (attribute === "custom.statuses") {
      const key = `${SITE_CODE}_${countryCode}_`;
      const matchingItem = item.name.includes(key);
      if (matchingItem) {
        output.push(item);
      }
    } else {
      output.push(item);
    }
  }
  return output;
}

export default function Checkbox(props: Props) {
  const t = useTranslate();
  const { countryCode } = useMarket();
  const { commerceNamePairs } = useNavigation().state;
  const {
    items = EMPTY_ITEMS,
    onChange,
    values = EMPTY_VALUES,
    attribute,
    name,
  } = props;
  const wrapper = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState<string>("");

  const searchable = useMemo(() => {
    if (name === "Category") {
      return false;
    }

    return items.length >= SEARCHABLE_THRESHOLD;
  }, [items, name]);

  const getOptionName = useCallback(
    (_name: string) => {
      const key = `${SITE_CODE}_${countryCode}_`;
      const name = _name.replace(key, "");
      return t(name, { strict: true }) || commerceNamePairs?.[name] || name;
    },
    [countryCode, commerceNamePairs, t],
  );

  const sortByDisplayedName = useCallback(
    (a: Item, b: Item) =>
      getOptionName(a.name) < getOptionName(b.name) ? -1 : 1,
    [getOptionName],
  );

  const options = useMemo(() => {
    const selection = filterElevateItems(
      items,
      attribute,
      countryCode,
      commerceNamePairs,
    );
    // Filter items starting with search term, or already selected
    const filteredItems = selection.filter((item) => {
      const { count, name } = item;
      const included = name
        .toString()
        .toLowerCase()
        .includes(search.toLowerCase());
      return (count && count > 0 && included) || values.includes(name);
    });
    if (name === "Category") {
      // Otherwise it will be sorted based on category key, e.g. NG_MÖBEL, NG_TABLES, etc, which causes incorrect sorting
      return filteredItems.sort(sortByDisplayedName);
    }
    return filteredItems;
  }, [
    items,
    name,
    attribute,
    countryCode,
    commerceNamePairs,
    search,
    values,
    sortByDisplayedName,
  ]);

  const virtualizer = useVirtualizer({
    count: options.length,
    getScrollElement: () => wrapper.current,
    estimateSize: () => 28,
    overscan: 10,
  });

  const rows = virtualizer.getVirtualItems();

  return (
    <div>
      {searchable ? (
        <Input
          type="text"
          fullWidth
          placeholder={t("filterSearchPlaceholder")}
          onChange={({ target }) => setSearch(target.value)}
          classNames={{
            wrapper: " md:pr-0 mb-3",
            input:
              "focus-visible:-outline-offset-2 h-8 placeholder:text-base py-0 pl-2 placeholder:text-black border-black",
          }}
        />
      ) : null}
      <div
        ref={wrapper}
        style={{ height: 20 + virtualizer.getTotalSize() }}
        className="max-h-[280px] min-h-[40px] w-full overflow-x-hidden overflow-y-auto pb-2 transition-[height] [contain:strict]"
      >
        <div
          style={{ height: virtualizer.getTotalSize() }}
          className="relative w-full"
        >
          {rows.length === 0 && (
            <p className="text-sm text-gray-200">
              {t("filterSearchNoResults")}
            </p>
          )}

          {rows.map(({ key, index, start }) => {
            const { name, label, count } = options[index];
            return (
              <div
                className="absolute top-0 left-0 mb-1 w-full"
                style={{
                  transform: `translateY(${start}px)`,
                }}
                data-index={index}
                key={key}
              >
                <NGGCheckbox
                  key={name}
                  name={name}
                  checked={values.includes(name)}
                  onChange={(e) => onChange(e, attribute)}
                  disabled={!count}
                  checkedIcon={<CheckIcon strokeWidth={1} className="size-4" />}
                  classNames={{
                    label:
                      "whitespace-nowrap grow ml-2 flex items-center w-full justify-between overflow-hidden",
                    checkbox: `cursor-pointer ${
                      values.includes(name) ? "text-black" : "text-gray-150"
                    }`,
                    wrapper: "p-0",
                  }}
                >
                  <div className="flex w-full">
                    <span className="mr-[2px] truncate text-base">
                      {label || getOptionName(name)}
                    </span>
                    <span className="text-gray mr-[2px] text-sm leading-5">{`(${count})`}</span>
                  </div>
                </NGGCheckbox>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
